"use client";
import React, { useEffect, useState } from "react";
import "./ContactForm.scss";
import { AiFillCloseCircle } from "react-icons/ai";

const WORK_CODE_OPTIONS = [
  {
    label: "Alfa Mall Damage or Faulty Product Received",
    value: "alfa_mall_damage_or_faulty_product_received",
  },
  {
    label: "Alfa Mall Product Not Received",
    value: "alfa_mall_product_not_received",
  },
  {
    label: "Product Not Booked but Amount Debited",
    value: "product_not_booked_but_amount_debited",
  },
  {
    label: "SBS Request Not Processed Through AlfaMall",
    value: "sbs_request_not_processed_through_alfamall",
  },
  {
    label: "Others",
    value: "others",
  },
];

const getOrderStatusSummary = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const nested = getOrderStatusSummary(data.data);
    if (nested) {
      return nested;
    }
  }
  // storefront/customer_order_status: { "status": "In Progress" }
  const rootKeys = Object.keys(data);
  if (
    rootKeys.length === 1 &&
    rootKeys[0] === "status" &&
    typeof data.status === "string" &&
    data.status.trim()
  ) {
    return data.status.trim();
  }

  const parts = [];
  if (typeof data.status === "string" && data.status.trim()) {
    parts.push(data.status.trim());
  }
  if (data.financial_status) {
    parts.push(`Payment: ${data.financial_status}`);
  }
  if (data.fulfillment_status) {
    parts.push(`Fulfillment: ${data.fulfillment_status}`);
  }
  if (data.order_status) {
    parts.push(String(data.order_status));
  }
  if (data.state != null && String(data.state).trim()) {
    parts.push(String(data.state).trim());
  }
  if (data.order_state != null && String(data.order_state).trim()) {
    parts.push(String(data.order_state).trim());
  }
  if (typeof data.message === "string" && data.message.trim()) {
    parts.push(data.message.trim());
  }
  if (parts.length) {
    return parts.join(" · ");
  }
  if (data.delivery_date) {
    return `Est. shipment: ${data.delivery_date}`;
  }
  if (data.name) {
    return `Order ${data.name} — details retrieved`;
  }
  return "Order found";
};

const normalizeOrdersFromApiResponse = (data) => {
  if (!data || typeof data !== "object") {
    return [];
  }
  // customer_order_status: { "status": [ { name, status }, ... ] }
  const statusAsRows =
    Array.isArray(data.status) &&
    data.status.length > 0 &&
    typeof data.status[0] === "object" &&
    data.status[0] !== null;
  const nestedStatusRows =
    data.data &&
    typeof data.data === "object" &&
    Array.isArray(data.data.status) &&
    data.data.status.length > 0 &&
    typeof data.data.status[0] === "object" &&
    data.data.status[0] !== null;

  const raw =
    (Array.isArray(data.orders) && data.orders) ||
    (statusAsRows ? data.status : null) ||
    (nestedStatusRows ? data.data.status : null) ||
    (data.data &&
      typeof data.data === "object" &&
      Array.isArray(data.data.orders) &&
      data.data.orders) ||
    null;
  if (!raw || !raw.length) {
    return [];
  }
  return raw
    .map((item, index) => ({
      name: item?.name != null ? String(item.name).trim() : "",
      status: item?.status != null ? String(item.status).trim() : "",
      key: `${item?.name ?? ""}-${item?.status ?? ""}-${index}`,
    }))
    .filter((row) => row.name || row.status);
};

const orderStatusChipClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (/dispatch|shipped|delivered|complete|approved|paid/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--success";
  }
  if (/progress|pending|process|hold|wait/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--pending";
  }
  if (/cancel|fail|reject|error/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--error";
  }
  return "contact-form__order-chip contact-form__order-chip--neutral";
};

const formatOrderStatusApiError = (errBody) => {
  if (!errBody || typeof errBody !== "object") {
    return null;
  }
  if (errBody.detail != null) {
    return typeof errBody.detail === "string"
      ? errBody.detail
      : JSON.stringify(errBody.detail);
  }
  const parts = [];
  for (const val of Object.values(errBody)) {
    if (Array.isArray(val)) {
      val.forEach((item) => {
        if (typeof item === "string") {
          parts.push(item);
        } else if (item != null) {
          parts.push(String(item));
        }
      });
    } else if (typeof val === "string") {
      parts.push(val);
    }
  }
  return parts.length ? parts.join(" ") : null;
};

const ContactForm = ({ onClose, backgroundColor = "#D62314" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    inquiryType: "",
    details: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    mobileNo: "",
    inquiryType: "",
    details: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobileNo: false,
    inquiryType: false,
    details: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState("");
  const [attachments, setAttachments] = useState([null, null]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([null, null]);

  const [orderNumber, setOrderNumber] = useState("");
  const [orderLookupLoading, setOrderLookupLoading] = useState(false);
  const [orderLookupResult, setOrderLookupResult] = useState("");
  const [orderLookupOrders, setOrderLookupOrders] = useState([]);
  const [orderLookupError, setOrderLookupError] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    } else {
      newErrors.name = "";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
      valid = false;
    } else {
      newErrors.email = "";
    }

    // Mobile number validation
    if (!formData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
      valid = false;
    } else if (!formData.mobileNo.startsWith("03")) {
      newErrors.mobileNo = "Mobile number must start with 03";
      valid = false;
    } else if (!/^03\d{9}$/.test(formData.mobileNo)) {
      newErrors.mobileNo = "Please enter a valid phone number (11 digits)";
      valid = false;
    } else {
      newErrors.mobileNo = "";
    }

    // Details validation
    if (!formData.inquiryType.trim()) {
      newErrors.inquiryType = "Select type is required";
      valid = false;
    } else {
      newErrors.inquiryType = "";
    }

    // Details validation
    if (!formData.details.trim()) {
      newErrors.details = "Message is required";
      valid = false;
    } else if (formData.details.trim().length < 10) {
      newErrors.details = "Message must be at least 10 characters";
      valid = false;
    } else {
      newErrors.details = "";
    }

    setErrors(newErrors);
    return valid;
  };

  useEffect(() => {
    return () => {
      attachmentPreviews.forEach((preview) => {
        if (preview) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [attachmentPreviews]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobileNo") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue.length <= 11) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else if (name === "name") {
      // Allow only letters and spaces, no numbers or special characters
      const alphabeticValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({
        ...formData,
        [name]: alphabeticValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleOrderNumberChange = (e) => {
    setOrderNumber(e.target.value);
    setOrderLookupResult("");
    setOrderLookupOrders([]);
    setOrderLookupError("");
  };

  const handleOrderLookup = async () => {
    const id = orderNumber.trim();
    if (!id) {
      setOrderLookupError("Please enter an order number.");
      setOrderLookupResult("");
      return;
    }

    setOrderLookupLoading(true);
    setOrderLookupError("");
    setOrderLookupResult("");
    setOrderLookupOrders([]);

    try {
      const url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/customer_order_status`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: id }),
      });

      if (!response.ok) {
        let message = "Could not fetch order status.";
        try {
          const errBody = await response.json();
          const formatted = formatOrderStatusApiError(errBody);
          if (formatted) {
            message = formatted;
          }
        } catch {
          if (response.status === 404) {
            message = "Order not found.";
          } else if (response.status === 400) {
            message = "Invalid order number. Please check and try again.";
          }
        }
        setOrderLookupError(message);
        return;
      }

      const data = await response.json();
      const ordersRows = normalizeOrdersFromApiResponse(data);
      if (ordersRows.length > 0) {
        setOrderLookupOrders(ordersRows);
        setOrderLookupResult("");
      } else {
        setOrderLookupOrders([]);
        const summary = getOrderStatusSummary(data);
        setOrderLookupResult(summary || "Order details loaded.");
      }
    } catch (err) {
      console.error("Order lookup failed:", err);
      setOrderLookupError("Something went wrong. Please try again.");
    } finally {
      setOrderLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (validateForm()) {
      // setIsSubmitting(true);
      // setSubmitStatus(null);
      setApiResponse("");

      try {
        const payload = new FormData();
        payload.append("name", formData.name);
        payload.append("email", formData.email);
        payload.append("phone", formData.mobileNo);
        payload.append("inquiry_type", formData.inquiryType);
        payload.append("details", formData.details);

        attachments.forEach((file) => {
          if (file) {
            payload.append("attachments", file);
          }
        });

        if (process.env.NODE_ENV === "development") {
          const logEntries = [];
          payload.forEach((value, key) => {
            logEntries.push(
              value instanceof File
                ? { key, file: value.name, type: value.type, size: value.size }
                : { key, value: String(value) }
            );
          });
          console.log("Final FormData payload:", logEntries);
        }

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND}/storefront/customer_inquiry`,
          {
            method: "POST",
            body: payload,
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("API response:", data);
          setSubmitStatus("success");
          setApiResponse(data.detail || "Thank you! We will contact you soon.");
          setTimeout(() => {
            onClose();
          }, 3000);
        } else {
          console.error("API error:", response.status);
          setSubmitStatus("error");
          setApiResponse("Failed to submit form. Please try again.");
        }
      } catch (error) {
        console.error("Request failed:", error);
        setSubmitStatus("error");
        setApiResponse("An error occurred. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleAttachmentChange = (event, index) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      return;
    }

    const nextAttachments = [...attachments];
    nextAttachments[index] = selectedFile;
    setAttachments(nextAttachments);

    const nextPreviews = [...attachmentPreviews];
    if (nextPreviews[index]) {
      URL.revokeObjectURL(nextPreviews[index]);
    }
    nextPreviews[index] = URL.createObjectURL(selectedFile);
    setAttachmentPreviews(nextPreviews);
  };

  const handleNumericKeyDown = (e) => {
    if (
      [8, 46, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 67 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 88 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 86 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }

    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const renderContent = () => {
    if (submitStatus === "success") {
      return (
        <div className="contact-form__success-container">
          <div className="contact-form__success-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="40" r="40" fill={backgroundColor} />
              <path
                d="M35 50L28 43M35 50L52 33"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="contact-form__success-message">{apiResponse}</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="contact-form__content">
        <h2 className="contact-form__title">Contact Us</h2>

        <div className="contact-form__field">
          <label htmlFor="orderNumberLookup">Order number</label>
          <div className="contact-form__order-row">
            <div className="contact-form__order-input-wrap">
              <input
                type="text"
                id="orderNumberLookup"
                name="orderNumberLookup"
                value={orderNumber}
                onChange={handleOrderNumberChange}
                placeholder="Enter order number"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!orderLookupLoading) {
                      handleOrderLookup();
                    }
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="contact-form__order-check-btn"
              style={{ backgroundColor }}
              onClick={handleOrderLookup}
              disabled={orderLookupLoading}
            >
              {orderLookupLoading ? "Checking…" : "Check status"}
            </button>
          </div>
          {orderLookupOrders.length > 0 && (
            <div
              className="contact-form__order-status-wrap"
              role="status"
              style={{ "--timeline-accent": backgroundColor }}
            >
              <span className="contact-form__order-status-label">Order Status</span>
              <ul className="contact-form__order-timeline">
                {orderLookupOrders.map((row, index) => (
                  <li key={row.key} className="contact-form__order-timeline-item">
                    <div className="contact-form__order-timeline-track" aria-hidden="true">
                      <span className="contact-form__order-timeline-dot" />
                      {index < orderLookupOrders.length - 1 ? (
                        <span className="contact-form__order-timeline-connector" />
                      ) : null}
                    </div>
                    <div className="contact-form__order-timeline-body">
                      <div className="contact-form__order-timeline-card">
                        <div className="contact-form__order-timeline-card-head">
                          <span className="contact-form__order-timeline-step">
                            Status
                          </span>
                          <span className={orderStatusChipClass(row.status)}>
                            {row.status || "—"}
                          </span>
                        </div>
                        <div className="contact-form__order-timeline-meta">
                          <span className="contact-form__order-timeline-meta-label">Order no.</span>
                          <span className="contact-form__order-timeline-meta-value">
                            {row.name || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {orderLookupResult && orderLookupOrders.length === 0 && (
            <div
              className="contact-form__order-status-wrap"
              role="status"
              style={{ "--timeline-accent": backgroundColor }}
            >
              <span className="contact-form__order-status-label">Order status</span>
              <ul className="contact-form__order-timeline">
                {orderLookupResult.split(" · ").map((part, chipIndex, arr) => (
                  <li
                    key={chipIndex}
                    className="contact-form__order-timeline-item contact-form__order-timeline-item--compact"
                  >
                    <div className="contact-form__order-timeline-track" aria-hidden="true">
                      <span className="contact-form__order-timeline-dot" />
                      {chipIndex < arr.length - 1 ? (
                        <span className="contact-form__order-timeline-connector" />
                      ) : null}
                    </div>
                    <div className="contact-form__order-timeline-body">
                      <div className="contact-form__order-timeline-card">
                        <span className="contact-form__order-chip contact-form__order-chip--success">
                          {part.trim()}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {orderLookupError && (
            <div className="contact-form__order-status-wrap" role="alert">
              <div className="contact-form__order-chips">
                <span className="contact-form__order-chip contact-form__order-chip--error">
                  {orderLookupError}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your name"
            className={errors.name && touched.name ? "error" : ""}
          />
          {errors.name && touched.name && (
            <p className="contact-form__error">{errors.name}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your email"
            className={errors.email && touched.email ? "error" : ""}
          />
          {errors.email && touched.email && (
            <p className="contact-form__error">{errors.email}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="mobileNo">
            Mobile Number <span className="required">*</span>
          </label>
          <input
            type="text"
            id="mobileNo"
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleNumericKeyDown}
            placeholder="03XXXXXXXXX"
            maxLength={11}
            inputMode="numeric"
            className={errors.mobileNo && touched.mobileNo ? "error" : ""}
          />
          {errors.mobileNo && touched.mobileNo && (
            <p className="contact-form__error">{errors.mobileNo}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="inquiryType">
            Select Type <span className="required">*</span>
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.inquiryType && touched.inquiryType ? "error" : ""}
          >
            <option value="" disabled>
              Select issue type
            </option>
            {WORK_CODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.inquiryType && touched.inquiryType && (
            <p className="contact-form__error">{errors.inquiryType}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="details">
            Message <span className="required">*</span>
          </label>
          <textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your message (minimum 10 characters)"
            rows="4"
            className={errors.details && touched.details ? "error" : ""}
          />
          {errors.details && touched.details && (
            <p className="contact-form__error">{errors.details}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label>Attachment images</label>
          <div className="contact-form__attachments">
            {[0, 1].map((index) => (
              <label key={index} className="contact-form__attachment-box">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleAttachmentChange(event, index)}
                />
                {attachmentPreviews[index] ? (
                  <img
                    src={attachmentPreviews[index]}
                    alt={`Attachment preview ${index + 1}`}
                    className="contact-form__attachment-preview"
                  />
                ) : (
                  <span>Upload Image</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="contact-form__submit-btn"
          disabled={isSubmitting}
          style={{ backgroundColor }}
        >
          {isSubmitting ? "Submitting..." : "Send Message"}
        </button>

        {submitStatus === "error" && (
          <p className="contact-form__status contact-form__status--error">
            {apiResponse}
          </p>
        )}
      </form>
    );
  };

  return (
    <div className="contact-form" style={{ "--background-color": backgroundColor }}>
      <div className="contact-form__header">
        <h3>How may I help you?</h3>
        <button
          className="contact-form__close-btn"
          onClick={onClose}
          aria-label="Close form"
        >
          <AiFillCloseCircle size={24} />
        </button>
      </div>

      <div className="contact-form__card">{renderContent()}</div>
    </div>
  );
};

export default ContactForm;
